import React from 'react';
import { DraftProfile, completeDraft } from '../../services/draftStorageService';
import './saved-drafts-popup.scss';

export interface SavedDraftsListProps {
  drafts: DraftProfile[];
  isLoading?: boolean;
  onSelectDraft: (draft: DraftProfile) => void;
  onDraftDeleted: () => void;
}

/** Relative time label in Arabic */
export function getDraftRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (minutes < 1) return 'الآن';
  if (minutes < 60) return `منذ ${minutes} ${minutes === 1 ? 'دقيقة' : 'دقائق'}`;
  if (hours < 24) return `منذ ${hours} ${hours === 1 ? 'ساعة' : 'ساعات'}`;
  if (days === 1) return 'أمس';
  return `منذ ${days} ${days <= 10 ? 'أيام' : 'يوم'}`;
}

const SavedDraftsList: React.FC<SavedDraftsListProps> = ({
  drafts,
  isLoading = false,
  onSelectDraft,
  onDraftDeleted,
}) => {
  const handleDelete = async (e: React.MouseEvent, draftId: string) => {
    e.stopPropagation();
    await completeDraft(draftId);
    onDraftDeleted();
  };

  if (isLoading) {
    return (
      <div className="saved-drafts-empty">
        <span className="empty-icon loading-spinner">⏳</span>
        <p>جاري تحميل المحادثات...</p>
      </div>
    );
  }

  if (drafts.length === 0) {
    return (
      <div className="saved-drafts-empty">
        <span className="empty-icon">📋</span>
        <p>لا توجد محادثات سابقة</p>
      </div>
    );
  }

  return (
    <div className="saved-drafts-list">
      {drafts.map(draft => (
        <div
          key={draft.id}
          className="draft-card"
          onClick={() => onSelectDraft(draft)}
          role="button"
          tabIndex={0}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onSelectDraft(draft);
            }
          }}
        >
          <div className={`draft-icon ${draft.flowType}`}>
            {draft.flowType === 'policy' ? '📋' : '🔧'}
          </div>
          <div className="draft-info">
            <div className="draft-flow-type">
              {draft.flowType === 'policy' ? 'وثيقة تأمين' : 'مطالبة'}
            </div>
            <div className="draft-step">{draft.lastStepLabel}</div>
            <div className="draft-time">{getDraftRelativeTime(draft.savedAt)}</div>
          </div>
          <button
            type="button"
            className="draft-delete"
            onClick={e => handleDelete(e, draft.id)}
            aria-label="حذف"
          >
            ✕
          </button>
        </div>
      ))}
    </div>
  );
};

export default SavedDraftsList;
